import { ReactElement, useState } from 'react';
import { Field, useFormikContext, getIn } from 'formik';
import { tile, iContactInfoDisplay } from '@interfaces/interfaces';
import { iThirdPartyFormValues } from '@services/ThirdPartyService';
import { iCourtFormValues } from '@services/CourtService';
import TileRadio from '@components/TileRadio/TileRadio';
import { TitleOptionsList, FormType } from '@constants';
import { TextInput, DropdownMenu, EmailFieldWithExperian, AddressField, ContactInputWithExperian, LicenceField, DateInput, Modal } from '@components';
import gridStyles from '@pages/forms/Grid.module.scss';

interface iContactInformation {
  displayObject: iContactInfoDisplay;
  currentStep: string;
}

function ContactInformation({ displayObject, currentStep }: iContactInformation): ReactElement {
  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];
  const extensionModal = (): ReactElement => (
    <>
      <div>
        <h4>Change of address? Remember to update your details with VicRoads</h4>
        <p>Have you moved recently? By law you must advise VicRoads of your new address within 14 days of moving.</p>
        <p>Fines sent to the address registered with VicRoads are 'deemed served', even if you no longer live there and did not receive them.</p>
        <p>This means that agencies can take enforcement action against you if you have not dealt with a fine sent to an old address.</p>
        <p>You can update both your licence and vehicle registration details online at VicRoads.</p>
        <div style={{ padding: '17px', margin: 'auto', width: '100%', textAlign: 'center' }}>
          <a
            style={{
              padding: '17px 32px',
              transition: 'all 0.5s ease',
              borderRadius: '2px',
              fontWeight: 600,
              fontFamily: 'VIC',
              textDecoration: 'none',
              display: 'inline-block',
              backgroundColor: '#00a9e1',
              border: '1px solid #00a9e1',
              textAlign: 'center' }}
            href="https://www.vicroads.vic.gov.au/licences/renew-replace-or-update/update-your-details/change-of-address"
            target="_blank"
            rel="noreferrer">
            Go to VicRoads
          </a>
        </div>
      </div>
    </>
  );
  const [modal, setModal] = useState({
    show: false,
    title: '',
    content: <></>,
    contentClass: '',
  });

  const isThirdParty = displayObject.mode === FormType.THIRDPARTY;

  const { values }: { values: iThirdPartyFormValues | iCourtFormValues } = useFormikContext();
  const { title, givenName, familyName, dateOfBirth, driverLicence, address, isAddressSameAsPostal, postalAddress, contactNumber, noEmailAddress, emailAddress, emailTo, receiveDocs, emailAddressWarning, contactNumberWarning, contactNumberSecondaryWarning } = displayObject.fields;
  if (values.NoEmail || values.NoEmailAddressThree) {
    values.EMAIL_TO = 'false';
  }
  return (
    <div className={gridStyles.form}>
      <div className="vfH2">Your details</div>
      <Modal close={() => setModal({ ...modal, show: false })} {...modal} />
      {!isThirdParty && (
      <div className={gridStyles.gridColFull}>
        <Field name={title.formikName} component={DropdownMenu} title="Title" isMandatory displayText={TitleOptionsList} defaultOption />
      </div>
)}
      <div className={gridStyles.gridColFull}>
        <Field name={givenName.formikName} component={TextInput} title="Given name(s)" isMandatory />
      </div>
      <div className={gridStyles.gridColFull}>
        <Field name={familyName.formikName} component={TextInput} title="Family name" isMandatory />
      </div>
      <div className={gridStyles.gridColHalf}>
        <Field name={dateOfBirth.formikName} component={DateInput} title="Date of birth" maxDate={new Date()} isMandatory />
      </div>
      <LicenceField
        name={driverLicence.formikName}
        title={driverLicence.title!}
        nameStateOfIssue="LicenceState"
        nameCountryOfIssue="StatecountryOfIssue"
        nameNoDriverLicence="NoDriverLicence"
        isMandatory
      />
      <div className={gridStyles.gridColFull}>
        <AddressField name={address.formikName} title={address.title!} manualAddressText="Enter your address manually" isMandatory />
      </div>
      {displayObject.mode === FormType.EXTENSION && (
        <div>
          <button
            type="button"
            style={{ border: 'none', color: '#0072ce', backgroundColor: 'transparent', cursor: 'pointer', marginTop: '5px', textDecoration: 'underline' }}
            onClick={() => {
                const modalTitle = 'Change of address? Remember to update your details with VicRoads';
                setModal({
                  show: true,
                  title: modalTitle,
                  content: extensionModal(),
                  contentClass: '',
                });
              }}>
            Have you moved recently?
          </button>
        </div>
      )}
      <Field
        name={isAddressSameAsPostal.formikName}
        component={TileRadio}
        tileData={TILE_DATA}
        orientationRow
        mobileCollapseRow={false}
        title={isAddressSameAsPostal.title}
        isMandatory
      />
      {getIn(values, isAddressSameAsPostal.formikName) === 'true' && (
        <AddressField name={postalAddress.formikName} title={postalAddress.title!} manualAddressText="Enter your address manually" isMandatory />
      )}
      <Field component={ContactInputWithExperian} name={contactNumber.formikName} title="Contact phone number" isMandatory numberTypeMandatory={!(displayObject.mode === FormType.COURT)} orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} hasPrimaryNumberType={!isThirdParty} secondaryContact={!isThirdParty} warningMessage={contactNumberWarning.formikName} warningMessageSec={contactNumberSecondaryWarning.formikName} />
      <Field component={EmailFieldWithExperian} name={emailAddress.formikName} title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} noEmailField={noEmailAddress.formikName} currentSP={currentStep} warningMessage={emailAddressWarning.formikName} />
      {!values[noEmailAddress.formikName] && (
        <div className={gridStyles.gridColFull}>
          <Field name={emailTo.formikName} component={TileRadio} tileData={TILE_DATA} title={emailTo.title} isMandatory orientationRow />
        </div>
      )}
      {displayObject.mode === FormType.EXTENSION && (
        <div className={gridStyles.gridColFull}>
          <Field name={receiveDocs.formikName} component={TileRadio} tileData={TILE_DATA} title={receiveDocs.title} orientationRow />
        </div>
      )}
    </div>
  );
}

export default ContactInformation;
